import { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import QRCode from 'qrcode'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { formatDate, formatDateTime } from '../utils/dateFormatter'
import { debounce } from '../utils/debounce'
import './QRCodes.css'

const escapeHtml = (s) => {
  if (s == null || s === '') return ''
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const tagPrintRef = (codeUuid) =>
  (codeUuid || '').replace(/-/g, '').slice(-8).toUpperCase() || '—'

/** Filter checked tags by numeric ID (odd/even, optional min–max). */
const applyPrintIdFilters = (items, { parity, startId, endId }) => {
  let out = [...items]
  const parseOpt = (v) => {
    if (v === '' || v == null) return null
    const n = parseInt(String(v), 10)
    return Number.isNaN(n) ? null : n
  }
  let start = parseOpt(startId)
  let end = parseOpt(endId)
  if (start != null && end != null && start > end) {
    const t = start
    start = end
    end = t
  }
  if (start != null) {
    out = out.filter((q) => q.id >= start)
  }
  if (end != null) {
    out = out.filter((q) => q.id <= end)
  }
  if (parity === 'odd') {
    out = out.filter((q) => q.id % 2 === 1)
  } else if (parity === 'even') {
    out = out.filter((q) => q.id % 2 === 0)
  }
  return out
}

const buildPrintFilterSummary = ({ parity, startId, endId }) => {
  const parts = []
  if (parity === 'odd') parts.push('Odd tag IDs only')
  else if (parity === 'even') parts.push('Even tag IDs only')
  const parseOpt = (v) => {
    if (v === '' || v == null) return null
    const n = parseInt(String(v), 10)
    return Number.isNaN(n) ? null : n
  }
  let s = parseOpt(startId)
  let e = parseOpt(endId)
  if (s != null && e != null && s > e) {
    const t = s
    s = e
    e = t
  }
  if (s != null || e != null) {
    parts.push(`ID range ${s ?? '…'}–${e ?? '…'}`)
  }
  return parts.length ? parts.join(' · ') : 'No ID filter (selected tags)'
}

/** Both From and To must be valid integers to print from the database by range. */
const parsePrintIdRangeBounds = (startId, endId) => {
  const parseNum = (v) => {
    if (v === '' || v == null) return null
    const n = parseInt(String(v).trim(), 10)
    return Number.isNaN(n) || !Number.isFinite(n) ? null : n
  }
  const s = parseNum(startId)
  const e = parseNum(endId)
  if (s == null || e == null) return null
  return { lo: Math.min(s, e), hi: Math.max(s, e) }
}

const PRINT_MODAL_LIST_CAP = 80

const QRCodes = () => {
  const navigate = useNavigate()
  const [qrcodes, setQrcodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCodes, setSelectedCodes] = useState(new Set())
  const [franchises, setFranchises] = useState([])
  const [selectedOwner, setSelectedOwner] = useState('')
  const [assigning, setAssigning] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  })
  const [filters, setFilters] = useState({
    is_active: '',
    is_car: '',
    search: '',
    owner_id: ''
  })
  const [searchInput, setSearchInput] = useState('')
  const [generateCount, setGenerateCount] = useState(1)
  const [vehicleType, setVehicleType] = useState('car') // 'car' or 'bike'
  const [generating, setGenerating] = useState(false)
  const [qrCodePreviews, setQrCodePreviews] = useState({})
  const [generatingPreviews, setGeneratingPreviews] = useState(new Set())
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false)
  const [printOptions, setPrintOptions] = useState({
    type: 'bike',
    copies: 1
  })
  const [printIdFilter, setPrintIdFilter] = useState({
    parity: 'all',
    startId: '',
    endId: ''
  })
  const [rangePreviewTags, setRangePreviewTags] = useState([])
  const [rangePreviewLoading, setRangePreviewLoading] = useState(false)
  const [rangePreviewTruncated, setRangePreviewTruncated] = useState(false)
  const [rangePreviewMax, setRangePreviewMax] = useState(1000)
  const previewsRef = useRef({})

  const idRangeBounds = useMemo(
    () => parsePrintIdRangeBounds(printIdFilter.startId, printIdFilter.endId),
    [printIdFilter.startId, printIdFilter.endId]
  )

  // Checked rows only, with optional odd/even/min/max on that set (when not using full DB range).
  const tagsMatchingPrintFilters = useMemo(
    () =>
      applyPrintIdFilters(
        qrcodes.filter((q) => selectedCodes.has(q.id)),
        printIdFilter
      ),
    [qrcodes, selectedCodes, printIdFilter]
  )

  const tagsForPrintModal = idRangeBounds ? rangePreviewTags : tagsMatchingPrintFilters
  const printModalListPreview = useMemo(
    () => tagsForPrintModal.slice(0, PRINT_MODAL_LIST_CAP),
    [tagsForPrintModal]
  )
  const printModalListOmit = Math.max(0, tagsForPrintModal.length - PRINT_MODAL_LIST_CAP)

  useEffect(() => {
    if (!isPrintModalOpen) {
      setRangePreviewTags([])
      setRangePreviewTruncated(false)
      setRangePreviewLoading(false)
      return
    }
    if (!idRangeBounds) {
      setRangePreviewTags([])
      setRangePreviewTruncated(false)
      setRangePreviewLoading(false)
      return
    }
    let cancelled = false
    setRangePreviewLoading(true)
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/qrcodes/print/by-id-range', {
          params: {
            start_id: idRangeBounds.lo,
            end_id: idRangeBounds.hi,
            parity: printIdFilter.parity
          }
        })
        if (cancelled) return
        if (res.data.success && res.data.data) {
          setRangePreviewTags(res.data.data.qrcodes || [])
          setRangePreviewTruncated(!!res.data.data.truncated)
          if (res.data.data.max) setRangePreviewMax(res.data.data.max)
        } else {
          setRangePreviewTags([])
          setRangePreviewTruncated(false)
        }
      } catch (err) {
        if (!cancelled) {
          setRangePreviewTags([])
          setRangePreviewTruncated(false)
          toast.error(err.response?.data?.message || 'Could not load tags for this range')
        }
      } finally {
        if (!cancelled) setRangePreviewLoading(false)
      }
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [isPrintModalOpen, idRangeBounds, printIdFilter.parity])

  // Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => filters, [
    filters.is_active,
    filters.is_car,
    filters.search,
    filters.owner_id
  ])

  // Debounced search handler
  const debouncedSearch = useMemo(
    () => debounce((value) => {
      setFilters(prev => ({ ...prev, search: value }))
      setPagination(prev => ({ ...prev, page: 1 }))
    }, 500),
    []
  )

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value
    setSearchInput(value)
    debouncedSearch(value)
  }, [debouncedSearch])

  const fetchQRCodes = useCallback(async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(memoizedFilters.is_active && { is_active: memoizedFilters.is_active }),
        ...(memoizedFilters.is_car && { is_car: memoizedFilters.is_car }),
        ...(memoizedFilters.search && { search: memoizedFilters.search }),
        ...(memoizedFilters.owner_id && { owner_id: memoizedFilters.owner_id })
      })

      const response = await api.get(`/qrcodes?${params}`)
      if (response.data.success) {
        setQrcodes(response.data.data.qrcodes)
        setPagination(response.data.data.pagination)
      }
    } catch (error) {
      console.error('Error fetching QR codes:', error)
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.limit, memoizedFilters])

  const fetchFranchises = async () => {
    try {
      const response = await api.get('/admin/users?role=franchise,franchise_tags&limit=100')
      if (response.data.success) {
        setFranchises(response.data.data.users)
      }
    } catch (error) {
      console.error('Error fetching franchise_tags users:', error)
    }
  }

  useEffect(() => {
    fetchQRCodes()
    fetchFranchises()
  }, [fetchQRCodes])

  // Update ref when previews change
  useEffect(() => {
    previewsRef.current = qrCodePreviews
  }, [qrCodePreviews])

  // Lazy load QR code previews - only generate when needed and in batches
  useEffect(() => {
    if (qrcodes.length === 0) {
      setQrCodePreviews({})
      previewsRef.current = {}
      setGeneratingPreviews(new Set())
      return
    }

    let cancelled = false
    const qrIds = new Set(qrcodes.map(qr => qr.id))

    // Generate previews in batches to avoid blocking UI
    const generatePreviewsBatch = async (batch) => {
      if (cancelled) return

      const newPreviews = {}

      for (const qr of batch) {
        if (cancelled) break

        // Skip if already exists (check ref for current state)
        if (previewsRef.current[qr.id]) {
          continue
        }

        // Mark as generating
        setGeneratingPreviews(prev => {
          if (prev.has(qr.id)) return prev
          return new Set(prev).add(qr.id)
        })

        try {
          const qrUrl = `${window.location.origin}/qr/${qr.code_uuid}`
          const qrDataUrl = await QRCode.toDataURL(qrUrl, {
            width: 80,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          })

          if (!cancelled && qrIds.has(qr.id) && !previewsRef.current[qr.id]) {
            newPreviews[qr.id] = qrDataUrl
          }
        } catch (error) {
          console.error('Error generating QR preview:', error)
        } finally {
          if (!cancelled) {
            setGeneratingPreviews(prev => {
              const next = new Set(prev)
              next.delete(qr.id)
              return next
            })
          }
        }
      }

      if (!cancelled && Object.keys(newPreviews).length > 0) {
        setQrCodePreviews(prev => {
          const updated = { ...prev, ...newPreviews }
          previewsRef.current = updated
          return updated
        })
      }
    }

    // Generate in batches of 3 to avoid blocking
    const batchSize = 3
    const timeouts = []
    for (let i = 0; i < qrcodes.length; i += batchSize) {
      const batch = qrcodes.slice(i, i + batchSize)
      // Use setTimeout to yield to browser between batches
      const timeout = setTimeout(() => {
        if (!cancelled) {
          generatePreviewsBatch(batch)
        }
      }, i * 100) // Stagger batches by 100ms
      timeouts.push(timeout)
    }

    return () => {
      cancelled = true
      timeouts.forEach(timeout => clearTimeout(timeout))
    }
  }, [qrcodes]) // Only regenerate when qrcodes array changes

  const handleGenerate = useCallback(async () => {
    try {
      setGenerating(true)
      const response = await api.post('/qrcodes/generate', {
        count: parseInt(generateCount),
        is_car: vehicleType === 'car'
      })

      if (response.data.success) {
        alert(`Successfully generated ${generateCount} ${vehicleType} tag(s)`)
        fetchQRCodes()
        setGenerateCount(1)
      }
    } catch (error) {
      console.error('Error generating QR codes:', error)
      alert(error.response?.data?.message || 'Error generating QR codes')
    } finally {
      setGenerating(false)
    }
  }, [generateCount, vehicleType, fetchQRCodes])

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      const allIds = new Set(qrcodes.map(qr => qr.id))
      setSelectedCodes(allIds)
    } else {
      setSelectedCodes(new Set())
    }
  }, [qrcodes])

  const handleAssignTags = async () => {
    if (selectedCodes.size === 0) {
      alert('Please select tags to assign')
      return
    }
    if (!selectedOwner) {
      alert('Please select an owner (franchise)')
      return
    }

    if (!window.confirm(`Assign ${selectedCodes.size} tags to the selected owner?`)) return

    try {
      setAssigning(true)
      const response = await api.post('/admin/qrcodes/assign', {
        tagIds: Array.from(selectedCodes),
        ownerId: parseInt(selectedOwner)
      })

      if (response.data.success) {
        alert('Tags assigned successfully!')
        setSelectedCodes(new Set())
        setSelectedOwner('')
        fetchQRCodes()
      }
    } catch (error) {
      console.error('Error assigning tags:', error)
      alert(error.response?.data?.message || 'Error assigning tags')
    } finally {
      setAssigning(false)
    }
  }

  const handleSelectOne = useCallback((id) => {
    setSelectedCodes(prev => {
      const newSelected = new Set(prev)
      if (newSelected.has(id)) {
        newSelected.delete(id)
      } else {
        newSelected.add(id)
      }
      return newSelected
    })
  }, [])

  const handlePrint = () => {
    setIsPrintModalOpen(true)
  }

  const executePrint = async () => {
    const bounds = parsePrintIdRangeBounds(printIdFilter.startId, printIdFilter.endId)
    let selectedQRs
    if (bounds) {
      try {
        const res = await api.get('/qrcodes/print/by-id-range', {
          params: {
            start_id: bounds.lo,
            end_id: bounds.hi,
            parity: printIdFilter.parity
          }
        })
        if (!res.data.success || !res.data.data) {
          toast.error(res.data.message || 'Could not load tags for printing')
          return
        }
        const { qrcodes: list, truncated, max } = res.data.data
        if (truncated) {
          toast.error(`Too many tags in this range (over ${max}). Narrow From/To or use odd/even.`)
          return
        }
        if (!list?.length) {
          toast.error('No tags in this range.')
          return
        }
        selectedQRs = list
      } catch (err) {
        toast.error(err.response?.data?.message || 'Could not load tags for printing')
        return
      }
    } else {
      selectedQRs = tagsMatchingPrintFilters
      if (selectedQRs.length === 0) {
        toast.error('Enter both From and To to print by database ID range, or select tags in the table.')
        return
      }
    }
    const { type, copies } = printOptions
    const printFilterLine = buildPrintFilterSummary(printIdFilter)

    // Configuration for sizes and labels
    const config = {
      bike: {
        width: '4in',
        height: '2in',
        perSheet: 22,
        cols_final: 4,
        rows_final: 5,
        sheetWidth: '19in',
        sheetHeight: '13in'
      },
      car: {
        width: '4in',
        height: '2in',
        perSheet: 22,
        cols_final: 4, // 19/4 = 4.75
        rows_final: 5, // 13/2.5 = 5.2. 4*5=20. User said 22, maybe tighter margins.
        sheetWidth: '19in',
        sheetHeight: '13in'
      }
    }[type]

    const widthIn = Number.parseFloat(String(config.width).replace(/in$/i, ''))
    const heightIn = Number.parseFloat(String(config.height).replace(/in$/i, ''))
    // Auto-scale QR square to fit short sticker heights (e.g. 2in).
    const qrSizeInRaw = Math.min(widthIn * 0.9, heightIn * 0.62)
    const qrSizeIn = Number.isFinite(qrSizeInRaw) ? Math.max(0.75, qrSizeInRaw) : 1.2

    // Generate QR code images for all selected codes
    const qrCodeImages = await Promise.all(
      selectedQRs.map(async (qr) => {
        try {
          const qrUrl = `${window.location.origin}/qr/${qr.code_uuid}`
          const qrDataUrl = await QRCode.toDataURL(qrUrl, {
            width: 400,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          })
          return { ...qr, qrImage: qrDataUrl }
        } catch (error) {
          console.error('Error generating QR code:', error)
          return { ...qr, qrImage: null }
        }
      })
    )

    // Expand selected tags based on copies requested
    const finalPrintList = []
    qrCodeImages.forEach(qr => {
      for (let i = 0; i < copies; i++) {
        finalPrintList.push(qr)
      }
    })

    // Chunk into pages so each sheet fits one printed page (no sticker breaks)
    const perSheet = config.cols_final * config.rows_final
    const pages = []
    for (let i = 0; i < finalPrintList.length; i += perSheet) {
      pages.push(finalPrintList.slice(i, i + perSheet))
    }

    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      toast.error('Could not open print window. Allow popups for this site and try again.')
      return
    }

    const printedAt = new Date().toLocaleString()
    const typeLabel = type === 'car' ? 'Car (2.5" × 3.5")' : 'Bike (2.5" × 3.5")'
    const summaryRows = selectedQRs
      .map((qr, idx) => {
        const vehicle = qr.linking?.vehicle_number || '—'
        const owner = qr.linking
          ? [qr.linking.first_name, qr.linking.last_name].filter(Boolean).join(' ') || qr.linking.mobile || '—'
          : qr.owner_business_name || qr.owner_name || '—'
        return `<tr>
          <td>${idx + 1}</td>
          <td>${qr.id}</td>
          <td><code>${escapeHtml(qr.code_uuid)}</code></td>
          <td>${escapeHtml(vehicle)}</td>
          <td>${escapeHtml(String(owner))}</td>
          <td>${copies}</td>
        </tr>`
      })
      .join('')

    const docTitle = `NAH44 print — ${selectedQRs.length} tag(s) × ${copies} copy(ies) — ${pages.length} sheet(s)`
    const indiaFlagSvg = '<svg class="india-flag-svg" viewBox="0 0 21 14" xmlns="http://www.w3.org/2000/svg"><rect width="21" height="4.67" fill="#FF9933"/><rect y="4.67" width="21" height="4.66" fill="#fff"/><rect y="9.33" width="21" height="4.67" fill="#138808"/><circle cx="10.5" cy="7" r="1.5" fill="#000080"/><circle cx="10.5" cy="7" r="1.1" fill="#fff" opacity="0.95"/></svg>'
    const iconStroke = '#000'
    const iconFill = '#fff'
    const iconParking = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="10" stroke="${iconStroke}" stroke-width="2.5" fill="none"/>
  <text x="12" y="16.5" text-anchor="middle" font-size="14" font-weight="bold" font-family="Arial, sans-serif" fill="${iconStroke}">R</text>
</svg>`;
    const iconRegistered = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M9 18h6" />
  <path d="M10 22h4" />
  <path d="M15.09 14c.18-1 .65-1.74 1.41-2.5A5.5 5.5 0 0018 8 6 6 0 006 8c0 1.3.5 2.6 1.5 3.5.76.76 1.23 1.5 1.41 2.5" />
  <path d="M12 14V11" />
  <path d="M12 11l-2-2.5" />
  <path d="M12 11l2-2.5" />
  <line x1="12" y1="2" x2="12" y2="4" />
  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
  <line x1="19.78" y1="4.22" x2="18.36" y2="5.64" />
  <line x1="2" y1="10" x2="4" y2="10" />
  <line x1="20" y1="10" x2="22" y2="10" />
</svg>`;
const iconWarning = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2">
  <path d="M12 3l10 18H2L12 3z"/>
  <path d="M12 9v4"/>
  <circle cx="12" cy="17" r="1"/>
</svg>`;
const iconPhone = `
<svg class="tag-icon" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2">
  <path d="M22 16.9v3a2 2 0 01-2.2 2A19.8 19.8 0 013 4.1 2 2 0 015 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.6a2 2 0 01-.5 2.1L9 9a16 16 0 006 6l1-1a2 2 0 012.1-.5c.8.3 1.7.5 2.6.6a2 2 0 011.7 2z"/>
</svg>`;
    // const iconParking = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 10h2v6H7zM15 10h2v6h-2z"/></svg>'
    // const iconRegistered = '<span class="tag-icon-circle">&#9415;</span>'
    // const iconWarning = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5"><path d="M12 8v4M12 16h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>'
    // const iconPhone = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>'
    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(docTitle)}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            
            body { 
              background: #f0f0f0;
              font-family: 'Outfit', sans-serif;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .print-job-summary {
              max-width: 10in;
              margin: 16px auto 24px;
              padding: 16px 20px;
              background: #fff;
              border: 1px solid #ccc;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.08);
              font-size: 12px;
              color: #222;
            }
            .print-job-summary h1 {
              font-size: 16px;
              margin: 0 0 10px 0;
              color: #1A365D;
            }
            .print-job-summary .meta { margin-bottom: 12px; line-height: 1.5; color: #444; }
            .print-job-summary table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 8px;
              font-size: 11px;
            }
            .print-job-summary th, .print-job-summary td {
              border: 1px solid #ddd;
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
            }
            .print-job-summary th { background: #f5f5f5; font-weight: 700; }
            .print-job-summary code { font-size: 10px; word-break: break-all; }

            .tag-ref-print {
              font-size: ${type === 'car' ? '6px' : '5px'};
              font-family: ui-monospace, monospace;
              color: #333;
              text-align: center;
              margin-top: 2px;
              letter-spacing: 0.04em;
              font-weight: 700;
              line-height: 1.1;
            }

            .sheet {
              width: ${config.sheetWidth};
              height: ${config.sheetHeight};
              background: white;
              margin: 20px auto;
              padding: 0.25in;
              display: grid;
              grid-template-columns: repeat(${config.cols_final}, ${config.width});
              grid-template-rows: repeat(${config.rows_final}, ${config.height});
              gap: 5px 5px;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
              page-break-after: always;
            }

            .tag-card {
              width: ${config.width};
              height: ${config.height};
              display: flex;
              border: 1px solid #ccc;
              overflow: hidden;
              position: relative;
              border-radius: 0;
              page-break-inside: avoid;
              break-inside: avoid;
            }

            .left-panel {
              width: 42%;
              background: #FF8C00 !important;
              padding: ${type === 'car' ? '6px 6px' : '4px 4px'};
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: space-between;
              -webkit-print-color-adjust: exact !important;
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }

            .white-qr-panel {
              width: 100%;
              background: white !important;
              border: 3px solid #FF8C00 !important;
              border-radius: 0;
              padding: 3px;
              display: flex;
              flex-direction: column;
              align-items: center;
              -webkit-print-color-adjust: exact !important;
            }
            .qr-wrapper {
              width: ${qrSizeIn}in;
              height: ${qrSizeIn}in;
              display: flex;
              align-items: center;
              justify-content: center;
              border: none;
              padding: 0;
              border-radius: 4px;
              overflow: hidden;
              max-width: 100%;
            }
            .bottom-legal {
              position: absolute;
              bottom: 6px;
              left: 0;
              right: 0;
              font-size: 6px;
              color: #000;
              text-align: center;
              font-weight: 800;
              line-height: 1.1;
              padding: 0 4px;
              white-space: nowrap;
              transform: scale(0.9);
            }

            .qr-wrapper img {
              width: 100%;
              height: 100%;
              display: block;
              border-radius: 4px;
            }

            .tag-branding-mini {
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: space-between;
              width: 100%;
              margin-top: 1px;
              padding: 0 2px;
            }
            .mini-logo-box {
              border: 1px solid #E89B2C !important;
              border-radius: 0;
              padding: 2px 5px;
              background: rgba(255,250,240,0.95) !important;
              display: inline-flex;
              align-items: center;
              gap: 0;
              white-space: nowrap;
              -webkit-print-color-adjust: exact !important;
            }
            .mini-logo-nah {
              color: #FF8C00 !important;
              font-weight: 900;
              font-size: ${type === 'car' ? '8px' : '7px'};
              text-shadow: 0 0 1px rgba(0,0,0,0.2);
            }
            .mini-logo-44 {
              color: #00458B !important;
              font-weight: 900;
              font-size: ${type === 'car' ? '8px' : '7px'};
              text-shadow: 0 0 1px rgba(0,0,0,0.2);
            }
            .india-flag-below { display: inline-flex; align-items: center; justify-content: center; }
            .india-flag-svg { width: ${type === 'car' ? '16px' : '14px'}; height: auto; display: block; border: 0.5px solid rgba(0,0,0,0.12); border-radius: 0; flex-shrink: 0; }

            .icons-row {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: ${type === 'car' ? '5px' : '4px'};
              margin: 1px 0 0 0;
            }
            .icons-row .tag-icon { width: ${type === 'car' ? '12px' : '10px'}; height: ${type === 'car' ? '12px' : '10px'}; display: block; }
            .icons-row .tag-icon-circle {
              font-size: ${type === 'car' ? '10px' : '8px'} !important;
              display: inline-flex; align-items: center; justify-content: center;
              width: ${type === 'car' ? '14px' : '12px'}; height: ${type === 'car' ? '14px' : '12px'};
              border: 1.5px solid #000;
              border-radius: 50%;
              background: #fff !important;
              color: #000 !important;
              font-weight: bold;
              -webkit-print-color-adjust: exact !important;
            }

            .bottom-text-left {
              font-size: ${type === 'car' ? '6.5px' : '5.5px'};
              text-align: center;
              line-height: 1.2;
              font-weight: 800;
              color: #000 !important;
              letter-spacing: 0.01em;
              padding: 0 2px;
              -webkit-print-color-adjust: exact !important;
            }
            .bottom-text-left .line1 { font-size: 1.1em; font-weight: 900; display: block; margin-bottom: 1px; }

            .right-panel {
              width: 58%;
              background: white !important;
              padding: ${type === 'car' ? '14px 16px 14px 14px' : '10px 12px 10px 12px'};
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              gap: ${type === 'car' ? '4px' : '2px'};
              position: relative;
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
              overflow: hidden;
            }

            /* Orange triangle corner decoration */
            .right-panel::before {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              width: 0;
              height: 0;
              border-style: solid;
              border-width: 0 ${type === 'car' ? '35px' : '25px'} ${type === 'car' ? '35px' : '25px'} 0;
              border-color: transparent #FF8C00 transparent transparent;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .right-header { margin-bottom: ${type === 'car' ? '10px' : '6px'}; }

            .nah-brand-framed {
              border: 3px solid #E89B2C !important;
              border-radius: 0;
              padding: ${type === 'car' ? '6px 14px' : '4px 10px'};
              display: inline-flex;
              align-items: center;
              gap: 2px;
              font-weight: 900;
              font-size: ${type === 'car' ? '22px' : '17px'};
              background: linear-gradient(180deg, #f5f0ea 0%, #e8e4df 100%) !important;
              box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), inset 0 0 0 1px rgba(255,255,255,0.6), 2px 2px 4px rgba(0,0,0,0.2), -1px -1px 2px rgba(255,255,255,0.4);
              white-space: nowrap;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .right-panel h1 {
              font-family: 'Outfit', sans-serif;
              font-size: ${type === 'car' ? '25px' : '18px'};
              font-weight: 900;
              color: #1A365D;
              margin: 0;
              line-height: 1;
              text-transform: uppercase;
              letter-spacing: -0.5px;
            }
            .right-panel h2 {
              font-family: 'Outfit', sans-serif;
              font-size: ${type === 'car' ? '28px' : '20px'};
              font-weight: 900;
              color: #FF8C00;
              margin: 0;
              line-height: 1;
              text-transform: uppercase;
              letter-spacing: -0.5px;
              white-space: nowrap;
            }
            .brand-nah {
              color: #FF8C00 !important;
              text-shadow: 2px 2px 3px rgba(0,0,0,0.35), 1px 1px 0 rgba(0,0,0,0.2);
            }
            .brand-44 {
              color: #00458B !important;
              text-shadow: 2px 2px 3px rgba(0,0,0,0.35), 1px 1px 0 rgba(0,0,0,0.2);
            }

            .scan-call-to-action {
              font-size: ${type === 'car' ? '21px' : '16px'};
              font-weight: 950;
              color: #1A479F !important;
              line-height: 1.05;
              margin: 0 0 4px 0;
              letter-spacing: -0.01em;
              text-transform: uppercase;
              font-family: 'Outfit', sans-serif;
            }
            .scan-call-to-action .highlight {
              color: #FF8C00 !important;
              -webkit-print-color-adjust: exact !important;
            }
            .scan-call-to-action .underline {
              text-decoration: none;
              border-bottom: ${type === 'car' ? '4px' : '3px'} solid #FF8C00;
              padding-bottom: 2px;
              display: inline-block;
            }

            .small-instructions {
              font-size: ${type === 'car' ? '7.5px' : '6.5px'};
              color: #333;
              line-height: 1.25;
              max-width: 95%;
              text-transform: uppercase;
              font-weight: 700;
              margin-top: ${type === 'car' ? '5px' : '3px'};
              letter-spacing: -0.1px;
            }

            @media print {
              body { background: transparent; }
              .print-job-summary { display: none !important; }
              .sheet { 
                margin: 0; 
                box-shadow: none;
                page-break-after: always;
                page-break-inside: avoid;
              }
              .sheet:last-child { page-break-after: auto; }
              .tag-card { page-break-inside: avoid; break-inside: avoid; }
              @page {
                size: ${config.sheetWidth} ${config.sheetHeight};
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-job-summary">
            <h1>Print job summary (not printed — for your records)</h1>
            <div class="meta">
              <strong>Printed:</strong> ${escapeHtml(printedAt)}<br/>
              <strong>Layout:</strong> ${escapeHtml(typeLabel)} · Sheet 13×19 in · <strong>${pages.length}</strong> sticker sheet(s)<br/>
              <strong>ID filter:</strong> ${escapeHtml(printFilterLine)}<br/>
              <strong>Unique tags:</strong> ${selectedQRs.length} · <strong>Copies per tag:</strong> ${copies} · <strong>Total stickers:</strong> ${finalPrintList.length}
            </div>
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Tag ID</th><th>UUID</th><th>Vehicle</th><th>Owner / note</th><th>Copies</th>
                </tr>
              </thead>
              <tbody>${summaryRows}</tbody>
            </table>
            <p style="margin-top:10px;font-size:10px;color:#666;">Each sticker shows ref <strong>TAG · last 8</strong> of UUID (no hyphens) under the QR for matching to this list.</p>
          </div>
          <div class="sticker-sheets">
          ${pages.map(page => `
          <div class="sheet">
            ${page.map(qr => `
              <div class="tag-card">
                <div class="left-panel">
                  <div class="white-qr-panel">
                    <div class="qr-wrapper">
                      ${qr.qrImage ? `<img src="${qr.qrImage}" alt="QR" />` : ''}
                    </div>
                    <div class="tag-ref-print">TAG · ${tagPrintRef(qr.code_uuid)}</div>
                    <div class="tag-branding-mini">
                      <span class="mini-logo-box"><span class="mini-logo-nah">NAH</span><span class="mini-logo-44">44</span></span>
                      <span class="india-flag-below">${indiaFlagSvg}</span>
                    </div>
                  </div>
                  <div class="icons-row">
                    ${iconParking} ${iconRegistered} ${iconWarning} ${iconPhone}
                  </div>
                  <div class="bottom-text-left">
                    <span class="line1">Wrong Parking. Emergency Contact.</span><br/>
                    any issue with the vehicle, Scan the QR.
                  </div>
                </div>
                <div class="right-panel">
                  <div class="right-header">
                    <div class="nah-brand-framed">
                      <span class="brand-nah">NAH</span><span class="brand-44">44</span>
                    </div>
                  </div>
                  <div class="scan-call-to-action">
                    Scan the code<br/>
                    to contact the<br/>
                    <span class="underline"><span class="highlight">vehicle</span> owner.</span>
                  </div>
                  <div class="small-instructions">
                    SCAN USING PHONE CAMERA, GOOGLE LENS OR ANY QR SCANNER APP,UPI APP SCANNER ,VISIT NAH44. COM FOR MORE.
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
          `).join('')}
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    const idsPrinted = [...new Set(selectedQRs.map((q) => q.id))]
    setTimeout(() => {
      printWindow.print()
      setIsPrintModalOpen(false)
      toast.success(
        `Print: ${selectedQRs.length} tag(s) × ${copies} copy(ies) = ${finalPrintList.length} stickers · ${pages.length} sheet(s) · ${typeLabel}`
      )
      void (async () => {
        try {
          await api.post('/qrcodes/print-record', { ids: idsPrinted })
          await fetchQRCodes()
        } catch (err) {
          toast.error(err.response?.data?.message || 'Could not save last printed time.')
        }
      })()
    }, 1000)
  }

  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }, [])

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  // Memoized table row component to prevent unnecessary re-renders
  const QRTableRow = memo(({ qr, isSelected, onSelect, onView }) => (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
        />
      </td>
      <td>{qr.id}</td>
      <td>
        {qrCodePreviews[qr.id] ? (
          <img
            src={qrCodePreviews[qr.id]}
            alt="QR Code"
            className="qr-code-preview"
            loading="lazy"
          />
        ) : (
          <div className="qr-code-placeholder">
            {generatingPreviews.has(qr.id) ? 'Generating...' : 'Loading...'}
          </div>
        )}
      </td>
      <td>
        <code className="code-uuid">{qr.code_uuid}</code>
      </td>
      <td>
        <span className={`badge ${qr.is_car ? 'badge-warning' : 'badge-warning'} `}>
          {qr.is_car ? 'Car' : 'Bike'}
        </span>
      </td>
      <td>
        <span className={`badge ${qr.is_active ? 'badge-success' : 'badge-error'} `}>
          {qr.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td>
        {qr.linking ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
              {[qr.linking.first_name, qr.linking.last_name].filter(Boolean).join(' ') || qr.linking.vehicle_number || 'Linked'}
            </span>
            {qr.linking.mobile && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {qr.linking.mobile}
              </span>
            )}
          </div>
        ) : qr.owner_business_name || qr.owner_name ? (
          <span className="owner-assigned" title={qr.owner_name}>
            📦 {qr.owner_business_name || qr.owner_name}
          </span>
        ) : (
          <span className="text-secondary" style={{ fontSize: '0.8rem' }}>Unassigned</span>
        )}
      </td>
      <td>{qr.linking?.vehicle_number || '-'}</td>
      <td>{formatDate(qr.created_at)}</td>
      <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
        {qr.last_printed_at ? formatDateTime(qr.last_printed_at) : '—'}
      </td>
      <td>
        <button
          className="btn-link"
          onClick={onView}
        >
          View
        </button>
      </td>
    </tr>
  ))

  QRTableRow.displayName = 'QRTableRow'

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading QR codes...</p>
      </div>
    )
  }

  return (
    <div className="admin-qrcodes animate-fadeIn">
      <div className="qr-header-section">
        <div>
          <h1>Tag Management</h1>
          <p className="admin-subtitle">Generate and manage QR tags</p>
        </div>
        <div className="qr-header-actions">
          <div className="vehicle-type-selector">
            <label className="vehicle-type-label">
              <input
                type="radio"
                name="vehicleType"
                value="car"
                checked={vehicleType === 'car'}
                onChange={(e) => setVehicleType(e.target.value)}
                disabled={generating}
              />
              <span>Car</span>
            </label>
            <label className="vehicle-type-label">
              <input
                type="radio"
                name="vehicleType"
                value="bike"
                checked={vehicleType === 'bike'}
                onChange={(e) => setVehicleType(e.target.value)}
                disabled={generating}
              />
              <span>Bike</span>
            </label>
          </div>
          <input
            type="number"
            min="1"
            max="100"
            value={generateCount}
            onChange={(e) => setGenerateCount(e.target.value)}
            className="qr-count-input"
            disabled={generating}
          />
          <button
            className="btn-primary"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate Tags'}
          </button>
          {selectedCodes.size > 0 && (
            <div className="assign-tags-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select
                value={selectedOwner}
                onChange={(e) => setSelectedOwner(e.target.value)}
                className="qr-count-input"
                style={{ width: '200px' }}
                disabled={assigning}
              >
                <option value="">Assign to Tag Distributor...</option>
                {franchises.map(f => (
                  <option key={f.id} value={f.id}>{f.name} ({f.business_name || f.email})</option>
                ))}
              </select>
              <button
                className="btn-primary"
                onClick={handleAssignTags}
                disabled={assigning || !selectedOwner}
              >
                {assigning ? 'Assigning...' : 'Assign Selected'}
              </button>
            </div>
          )}
          <button
            type="button"
            className="btn-primary btn-success"
            onClick={handlePrint}
          >
            🖨️ Print tags
            {selectedCodes.size > 0 ? ` (${selectedCodes.size} selected)` : ''}
          </button>
          <Link to="/admin/tag-scan" className="btn-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            📷 Scan sticker (lookup ID)
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="qr-filters">
          <input
            type="text"
            placeholder="Search by UUID or Vehicle Number..."
            value={searchInput}
            onChange={handleSearchChange}
          />
          <select
            value={filters.is_active}
            onChange={(e) => handleFilterChange('is_active', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select
            value={filters.is_car}
            onChange={(e) => handleFilterChange('is_car', e.target.value)}
          >
            <option value="">All Types</option>
            <option value="true">Car</option>
            <option value="false">Bike</option>
          </select>
          <select
            value={filters.owner_id}
            onChange={(e) => handleFilterChange('owner_id', e.target.value)}
          >
            <option value="">All Distributors</option>
            {franchises.map(f => (
              <option key={f.id} value={f.id}>{f.business_name || f.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* QR Codes Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            Tags ({pagination.total})
            {selectedCodes.size > 0 && (
              <span className="selected-count">
                {selectedCodes.size} selected
              </span>
            )}
          </h2>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedCodes.size === qrcodes.length && qrcodes.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>ID</th>
                <th>QR Code</th>
                <th>Code UUID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Vehicle Number</th>
                <th>Created At</th>
                <th>Last printed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {qrcodes.length === 0 ? (
                <tr>
                  <td colSpan="11" className="empty-state">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📱</div>
                    <p>No QR codes found</p>
                  </td>
                </tr>
              ) : (
                qrcodes.map(qr => (
                  <QRTableRow
                    key={qr.id}
                    qr={qr}
                    isSelected={selectedCodes.has(qr.id)}
                    onSelect={() => handleSelectOne(qr.id)}
                    onView={() => navigate(`/admin/qrcodes/${qr.id}`)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="pagination-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Items per page:</label>
              <select
                value={pagination.limit}
                onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer'
                }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <button
              className="btn-secondary"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.pages} ({pagination.total} total)
            </span>
            <button
              className="btn-secondary"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Print Options Modal */}
      {isPrintModalOpen && (
        <div className="modal-overlay" onClick={() => setIsPrintModalOpen(false)}>
          <div
            className="modal-content print-stickers-modal"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '520px', width: '100%' }}
          >
            <div className="modal-header">
              <h2>Print stickers</h2>
              <button type="button" className="modal-close" onClick={() => setIsPrintModalOpen(false)}>×</button>
            </div>

            <div className="print-stickers-modal-body">
            <div className="form-group">
              <label>Sticker Type</label>
              <div className="vehicle-type-selector" style={{ marginTop: '0.5rem' }}>
                <label className="vehicle-type-label">
                  <input
                    type="radio"
                    name="printType"
                    value="bike"
                    disabled={true}
                    checked={printOptions.type === 'bike'}
                    onChange={() => setPrintOptions(prev => ({ ...prev, type: 'bike' }))}
                  />
                  <span>Bike (2" x 4")</span>
                </label>
                <label className="vehicle-type-label">
                  <input
                    type="radio"
                    name="printType"
                    value="car"
                    checked={printOptions.type === 'car'}
                    onChange={() => setPrintOptions(prev => ({ ...prev, type: 'car' }))}
                  />
                  <span>Car (2" x 4")</span>
                </label>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <label>Copies per selected tag</label>
              <input
                type="number"
                min="1"
                max="50"
                value={printOptions.copies}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  const copies = Number.isFinite(n) ? Math.min(50, Math.max(1, n)) : 1
                  setPrintOptions(prev => ({ ...prev, copies }))
                }}
                className="qr-count-input"
                style={{ width: '100%', marginTop: '0.5rem' }}
              />
            </div>

            <div className="form-group" style={{ marginTop: '1.25rem' }}>
              <label>Print by database tag ID</label>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.5rem' }}>
                Enter <strong>From</strong> and <strong>To</strong> to load every tag in that range from the server (no need to check rows). Odd/even applies to that range. Leave either blank to print only rows you checked in the table instead.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem', alignItems: 'center' }}>
                <select
                  value={printIdFilter.parity}
                  onChange={(e) => setPrintIdFilter((p) => ({ ...p, parity: e.target.value }))}
                  style={{
                    padding: '0.45rem 0.6rem',
                    borderRadius: '0.5rem',
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="all">All IDs</option>
                  <option value="odd">Odd IDs only</option>
                  <option value="even">Even IDs only</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                  From
                  <input
                    type="number"
                    placeholder="Min ID"
                    value={printIdFilter.startId}
                    onChange={(e) => setPrintIdFilter((p) => ({ ...p, startId: e.target.value }))}
                    style={{ width: '88px', padding: '0.35rem' }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                  To
                  <input
                    type="number"
                    placeholder="Max ID"
                    value={printIdFilter.endId}
                    onChange={(e) => setPrintIdFilter((p) => ({ ...p, endId: e.target.value }))}
                    style={{ width: '88px', padding: '0.35rem' }}
                  />
                </label>
              </div>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.35rem', marginBottom: 0 }}>
                {buildPrintFilterSummary(printIdFilter)}
                {rangePreviewLoading && idRangeBounds ? ' · Loading…' : (
                  <>
                    {' · '}
                    <strong>{tagsForPrintModal.length}</strong> tag(s) will print
                    {idRangeBounds && rangePreviewTruncated ? (
                      <span style={{ color: 'var(--error-color, #f87171)' }}>
                        {' '}(over {rangePreviewMax} — narrow the range)
                      </span>
                    ) : null}
                  </>
                )}
              </p>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem', display: 'block' }}>
                {idRangeBounds
                  ? `Tags in this print job (${tagsForPrintModal.length} from database range)`
                  : `Tags in this print job (${tagsForPrintModal.length} of ${selectedCodes.size} checked)`}
              </label>
              <div
                style={{
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  padding: '0.35rem 0.5rem',
                  background: 'var(--bg-input)',
                  fontSize: '0.8rem'
                }}
              >
                {rangePreviewLoading && idRangeBounds ? (
                  <div style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>Loading tags…</div>
                ) : printModalListPreview.length === 0 ? (
                  <div style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>
                    {idRangeBounds ? 'No tags in this range.' : 'Select tags in the table, or set From and To.'}
                  </div>
                ) : (
                  <>
                    {printModalListPreview.map(qr => {
                    const vehicle = qr.linking?.vehicle_number
                    const owner = qr.linking
                      ? [qr.linking.first_name, qr.linking.last_name].filter(Boolean).join(' ') || qr.linking.mobile
                      : qr.owner_business_name || qr.owner_name
                    return (
                      <div
                        key={qr.id}
                        style={{
                          padding: '0.4rem 0.25rem',
                          borderBottom: '1px solid var(--border-color)',
                          lineHeight: 1.35
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>#{qr.id}</span>
                        {' · '}
                        <code className="code-uuid" style={{ fontSize: '0.75rem' }}>{qr.code_uuid}</code>
                        {vehicle && (
                          <span style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                            Vehicle: {vehicle}
                          </span>
                        )}
                        {owner && (
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', display: 'block' }}>
                            {owner}
                          </span>
                        )}
                        <span style={{ color: 'var(--primary-color)', fontSize: '0.75rem' }}>
                          × {printOptions.copies} sticker{printOptions.copies !== 1 ? 's' : ''} · ref TAG · {tagPrintRef(qr.code_uuid)}
                        </span>
                      </div>
                    )
                  })}
                    {printModalListOmit > 0 && (
                      <div style={{ padding: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        … and {printModalListOmit} more (all included when you print)
                      </div>
                    )}
                  </>
                )}
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                Total stickers: {tagsForPrintModal.length * printOptions.copies} · Sheet: 13×19 in · Summary in print tab (not on paper). Last printed is updated after you print.
              </p>
            </div>
            </div>

            <div className="print-stickers-modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setIsPrintModalOpen(false)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={executePrint}
                disabled={
                  rangePreviewLoading ||
                  rangePreviewTruncated ||
                  (idRangeBounds ? rangePreviewTags.length === 0 : tagsMatchingPrintFilters.length === 0)
                }
                style={{ flex: 1 }}
              >
                Print Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QRCodes

